import type { LicenseStatus, SubscriptionOverview, UsageLimit } from "@closer/shared";
import { LICENSE_STATUS_NAMES } from "@closer/shared";
import { Badge, Card, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { SubscriptionActionsCard } from "./SubscriptionActionsCard";
import { SubscriptionActivitySection } from "./SubscriptionActivitySection";

/**
 * ABONELİK VE KULLANIM — GENEL BAKIŞ (CLOS-197 `[173]`) + DURUM/AKSİYON GÖRÜNÜMÜ
 * (CLOS-931, CLOS-203 `[177]`).
 *
 * ### ⚠ Genel bakış BLOĞU hâlâ hiçbir şey değiştirmiyor
 *
 * CLOS-197'nin kapsam sınırı geçerli: paket/durum/dönem/kullanım bloğu salt projeksiyon,
 * kendi başına `useMutation` taşımıyor. Dönem sonu iptal/vazgeçme AKSİYONU ve ödeme
 * sorunu BANNER'ı ayrı bir bileşende (`SubscriptionActionsCard`) — CLOS-203'ün gerçekten
 * var olan `subscription.plan` komutlarına YÖNLENDİRME, kartın *"yetkili command
 * akışlarına yönlendirir"* dediği tam olarak budur. (Eski not *"dosyada tek bir
 * useMutation yok"* diyordu; CLOS-203 o an henüz yoktu.)
 *
 * ### ⚠ GÖSTERİLMEYENLER — uydurmamak bir tasarım kararı
 *
 * Fiyat/para birimi (veri hiç yok), depolama/AI sayaçları (CLOS-200) ve ek haklar
 * (CLOS-201) burada YOK. Gerekçeler `packages/shared/src/schemas/subscription.ts`'te
 * ölçümleriyle yazılı. Boş bir alan bile çizildiğinde olmayan bir özelliği varmış gibi
 * gösterir.
 */
export function SubscriptionOverviewPage() {
  const { activeTenantId } = useAuth();
  const { preferences } = usePreferences();

  const sorgu = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "subscription-overview")
      : ["t", "yok", "subscription-overview"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<SubscriptionOverview | null>("/api/subscription/overview", {
          tenantId: activeTenantId!,
        })
      ).data,
  });

  /**
   * Hata sunumu ORTAK KAPIDAN. 403 burada "bu ekranı görme yetkiniz yok" demek —
   * `subscription.view` korunan sınıfta ve standart üyeye düşmüyor.
   */
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Abonelik bilgilerini görme yetkiniz yok",
            description: "Bu ekran ayrı bir yetki gerektirir; organizasyon yöneticinizle görüşün.",
          },
          error: {
            title: "Abonelik bilgileri yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Abonelik bilgileri yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (hataDurumu) {
    return <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />;
  }

  const bakis = sorgu.data ?? null;

  /**
   * ⚠ ABONELİK YOKLUĞU BİR HATA DEĞİL, KENDİ DURUMU.
   *
   * Sunucu `null` döndüğünde alan var ama yaşayan aboneliği yok. Hata gibi gösterseydik
   * kullanıcı sistemin bozuk olduğunu sanırdı; uydurma bir varsayılan plan gösterseydik
   * gerçek bir arıza (aboneliksiz doğan alan) görünmez olurdu.
   */
  if (!bakis) {
    return (
      <Card>
        <p data-testid="abonelik-yok" className="text-sm text-fg-muted">
          Bu çalışma alanının etkin bir aboneliği bulunmuyor.
        </p>
      </Card>
    );
  }

  return (
    <section className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">Abonelik ve Kullanım</h1>
        <p className="text-sm text-fg-muted">
          Bu çalışma alanının planı, lisans durumu ve desteklenen kullanım özetleri.
        </p>
      </div>

      <SubscriptionActionsCard
        tenantId={activeTenantId!}
        status={bakis.status}
        // ⚠ `bakis.scheduledChange` FARKLI bir kavram (CLOS-255 plan düşürme planı) —
        // burada gösterilen, mevcut dönemin (dolayısıyla iptalin) ne zaman biteceği.
        periodEndsAt={formatDate(bakis.currentPeriodEnd, preferences.locale)}
      />

      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <span data-testid="paket-adi" className="text-base font-medium text-fg-strong">
                {bakis.packageName}
              </span>
              <span className="text-sm text-fg-muted">{bakis.packageDescription}</span>
            </div>
            <DurumRozeti status={bakis.status} />
          </div>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-soft">Dönem</dt>
            <dd data-testid="donem" className="text-fg">
              {formatDate(bakis.currentPeriodStart, preferences.locale)} —{" "}
              {formatDate(bakis.currentPeriodEnd, preferences.locale)}
            </dd>
            {/* ⚠ Deneme bitişi yalnız DENEME varken çiziliyor; `null` bir tarih değildir. */}
            {bakis.trialEndsAt ? (
              <>
                <dt className="text-fg-soft">Deneme bitişi</dt>
                <dd data-testid="deneme-bitisi" className="text-fg">
                  {formatDate(bakis.trialEndsAt, preferences.locale)}
                </dd>
              </>
            ) : null}
          </dl>

          {/*
            ⚠ PLANLANMIŞ DEĞİŞİKLİK, mevcut planın YANINDA ve ayrı bir cümleyle.
            Aynı satırda gösterseydik kullanıcı hangi planda olduğunu karıştırırdı.
          */}
          {bakis.scheduledChange ? (
            <p data-testid="planlanan-degisiklik" className="text-sm text-fg-muted">
              {formatDate(bakis.scheduledChange.effectiveAt, preferences.locale)} tarihinde{" "}
              <span className="font-medium text-fg-strong">
                {bakis.scheduledChange.packageName}
              </span>{" "}
              planına geçilecek.
            </p>
          ) : null}
        </div>
      </Card>

      <Card title="Kullanım">
        <div className="flex flex-col gap-4">
          {/*
            ⚠ İKİ AYRI KOTA, tek sayaç değil (CLOS-836 · alt görev CLOS-973). Fark ZAMAN ve
            DURUM ekseninde: aktif kapasite arşivlemeyle serbest kalıyor, dönemsel oluşturma
            hakkı yalnız dönem yenilenince geri geliyor. Ekranda tek satıra indirseydik
            "ayda 5 sözleşme" satan bir paket, kullanıcı arşivledikçe sınırsız görünürdü.
          */}
          <KullanimSatiri
            testId="kullanim-sozlesme"
            baslik="Aktif sözleşme kapasitesi"
            deger={bakis.usage.contracts}
            aciklama="Arşivlenmemiş her sözleşme bu kapasiteyi tutar; arşivlemek yeniden serbest bırakır."
          />
          <KullanimSatiri
            testId="kullanim-sozlesme-olusturma"
            baslik="Dönemsel sözleşme oluşturma hakkı"
            deger={bakis.usage.contractCreations}
            aciklama={`Bu dönemde açılan sözleşmeler sayılır ve ${formatDate(
              bakis.currentPeriodEnd,
              preferences.locale,
            )} tarihinde yenilenir. Arşivlemek harcanmış hakkı geri vermez.`}
          />
          <KullanimSatiri
            testId="kullanim-koltuk"
            baslik="İç kullanıcı"
            deger={bakis.usage.internalSeats}
          />
          {/*
            ⚠ AI KREDİSİ ARTIK BURADA (KARAR 04 · KARAR 12). Bir süre yoktu ve gerekçesi
            doğruydu: kaynağı yazılmamış bir sayacı sıfır göstermek, kullanımın sıfır
            olduğunu SÖYLEMEK olurdu. Kaynak yazıldı ve motor onu harcıyor; şimdi gizlemek,
            kullanıcının parasını harcayan bir sayacı ondan saklamak olurdu — kredisi biten
            kullanıcı bunu ancak analizi reddedildiğinde öğrenirdi.
          */}
          <KullanimSatiri
            testId="kullanim-ai-kredisi"
            baslik="AI kredisi"
            deger={bakis.usage.aiCredits}
          />
          {/*
            ⚠ Depolama sayacı HÂLÂ YOK: kaynağı (CLOS-200) yazılmadı ve gerekçe aynen
            duruyor.
          */}
        </div>
      </Card>

      {/* ABONELİK/KULLANIM/EK HAK/FATURALAMA AKTİVİTE GEÇMİŞİ (CLOS-286 `[181]`). */}
      <SubscriptionActivitySection subscriptionId={bakis.id} />
    </section>
  );
}

/**
 * Lisans durumu rozeti.
 *
 * ⚠ Metin `LICENSE_STATUS_NAMES`'ten, bu dosyadan DEĞİL — sözlük `@closer/shared`'da ve
 * sunucu da aynı sözlüğü kullanıyor. Burada kendi metnimizi yazsaydık iki taraf gün gelip
 * ayrışırdı (kriter 6: UI string uydurmaz).
 *
 * ⚠ TON durumun ANLAMINDAN türüyor: dikkat isteyen durumlar (ödeme bekleniyor, ek süre,
 * dönem sonunda iptal) uyarı, erişimi kesenler (kısıtlı, sona erdi) tehlike. Hepsini nötr
 * çizseydik kullanıcı bir sorunu olduğunu rengin değil yalnız metnin dikkatli okunmasından
 * anlardı.
 */
function DurumRozeti({ status }: { status: LicenseStatus }) {
  const ton =
    status === "active" || status === "trialing"
      ? "success"
      : status === "restricted" || status === "expired"
        ? "danger"
        : "warning";

  /**
   * ⚠ `data-testid` SARMALAYICIDA, `Badge`'in üstünde değil.
   *
   * `Badge` fazladan prop'ları iletmiyor (yalnız `tone`, `children`, `className`) ve
   * JSX `data-*` niteliklerini tip kontrolünden geçirdiği için bu sessizce kaybolurdu:
   * test yeşil olmaz, ekranda da bir şey değişmezdi — ama testid hiç render edilmezdi.
   * "Kodda var, ekranda yok" sınıfının küçük hâli.
   */
  return (
    <span data-testid="lisans-durumu">
      <Badge tone={ton}>{LICENSE_STATUS_NAMES[status]}</Badge>
    </span>
  );
}

/**
 * Tek bir kullanım satırı.
 *
 * ⚠ `limit === null` SINIRSIZ demek, "bilinmiyor" değil. "—" ya da boş göstermek, sınırsız
 * bir hakkı belirsizlik gibi okuturdu.
 */
function KullanimSatiri({
  testId,
  baslik,
  deger,
  aciklama,
}: {
  testId: string;
  baslik: string;
  deger: UsageLimit;
  /**
   * Sayacın SEMANTİĞİ — neyin serbest bıraktığı, neyin yenilendiği.
   *
   * ⚠ İki sözleşme kotası aynı karta yan yana geldiğinde (CLOS-973) rakamlar birbirine
   * benziyor; farkı anlatan tek şey bu cümle. Olmasaydı kullanıcı arşivlemenin dönemsel
   * oluşturma hakkını da geri verdiğini sanardı.
   */
  aciklama?: string;
}) {
  const sinirsiz = deger.limit === null;
  const oran = sinirsiz
    ? 0
    : Math.min(100, Math.round((deger.used / Math.max(1, deger.limit!)) * 100));
  const doldu = !sinirsiz && deger.limit! > 0 && deger.used >= deger.limit!;
  /**
   * ⚠ HİÇ HAK YOK ile HAK DOLDU AYRI ŞEYLER — canlı turda ölçüldü (4 Eylül).
   *
   * AI kredisi 0/0 iken ekran "Doldu" diyordu. Kullanıcı bunu "kotamı bitirdim" diye
   * okuyor ve bir sonraki dönemi bekliyor; oysa gerçek şu ki paketinde o hak HİÇ YOK ve
   * beklemekle bir şey değişmeyecek. İki durumun sonraki adımı da farklı: biri "dönem
   * yenilenince kullanırsın", öteki "paketine bu hak eklenmeli".
   */
  const hakYok = !sinirsiz && deger.limit === 0;

  return (
    <div data-testid={testId} className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-fg-strong">{baslik}</span>
        <span className="flex items-baseline gap-2">
          {/*
            ⚠ DOLULUK METİNLE de yazılıyor: aşağıdaki çubuğun kırmızıya dönmesi tek başına
            bir bilgi taşıyıcısı olamaz (kart: "progress/state yalnız renkle anlatılmaz").
            Renk körü ya da yüksek kontrast kipindeki kullanıcı dolu kotayı okuyamazdı.
          */}
          {doldu ? (
            <span data-testid={`${testId}-doldu`} className="text-xs font-medium text-danger">
              Doldu
            </span>
          ) : null}
          {hakYok ? (
            <span data-testid={`${testId}-hak-yok`} className="text-xs font-medium text-warning">
              Pakette yok
            </span>
          ) : null}
          <span className="text-sm tabular-nums text-fg-muted">
            {sinirsiz ? `${deger.used} · sınırsız` : `${deger.used} / ${deger.limit}`}
          </span>
        </span>
      </div>
      {aciklama ? <p className="text-xs text-fg-soft">{aciklama}</p> : null}
      {/*
        ⚠ Çubuk yalnız SINIRLI hakta çiziliyor: sınırsız bir hakkın doluluk oranı yok ve
        boş bir çubuk "hiç kullanılmamış" gibi okunurdu.
      */}
      {sinirsiz ? null : (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={oran >= 100 ? "h-full bg-danger" : "h-full bg-accent"}
            style={{ width: `${oran}%` }}
          />
        </div>
      )}
    </div>
  );
}
